import axios from 'axios'
import { validateAndFilterRoutingTables } from "../../../helpers/cloudPropagationValidation";
import {
  buildNexthopSettingsPayload,
  type NexthopRetryFormValues,
  type NexthopRetryPayload,
} from "../../../helpers/cloudPropagationHelpers";
import type { ApiInstanceLimits } from "../../../helpers/instanceLimits";

const API_URL = process.env.REACT_APP_API_URL || "";

// Dev-only: serve local BGP Visibility fixtures instead of the S3-backed backend.
// Build-time-inlined so `if (false)` branches (and their require) are dropped in prod.
const MOCK_BGP = process.env.REACT_APP_MOCK_BGP === "true";
const MOCK_BGP_VOLUME = process.env.REACT_APP_MOCK_BGP_VOLUME === "true";
export interface ReflectorBgpConfig {
    localAs: number;
    routerId: string;
    routerId2?: string;
}

/**
 * GET `/v2/reflector/instances/:id/bgp-config` — may include `reflectorInstance.instanceLimits`.
 */
export interface ReflectorBgpConfigGetResponse {
    localAs?: number | null;
    routerId?: string | null;
    reflectorInstanceId?: number;
    reflectorInstance?: {
        id: number;
        name?: string;
        instanceLimits?: ApiInstanceLimits;
    };
    [key: string]: unknown;
}

export interface ReflectorBgpNeighbor {
    neighborAddress: string;
    interfaceAddressId: number;
    peerAs: number;
    localAs: number;
    description: string;
    operatingMode: string;
    holdTime: number;
    keepAliveInterval: number;
    connectRetry: number;
    gracefulRestart: boolean;
    addPath: boolean;
    BFD: boolean;
    // bfdInterval == BFD desired_min_tx_interval (ms). Sent by legacy clients;
    // also acts as fallback when bfdRequiredMinRxInterval is not provided.
    bfdInterval: number;
    // bfdRequiredMinRxInterval == BFD required_min_rx_interval (ms). Optional
    // on the wire: backends that don't receive it fall back to bfdInterval.
    bfdRequiredMinRxInterval?: number | null;
    bfdMultiplier: number;
    password?: string | null;
}

export interface CtxrConfig {
    reflectorInstanceId: number;
    interfaceAddressId: number;
}

export interface BgpNeighbor {
    base_config: ReflectorBgpNeighbor;
    ctxr_config: CtxrConfig | CtxrConfig[];
}

export interface ReflectorLicense {
    tenantId: number;
    subscription?: string;
    description: string;
    type: string;
    startDate: string;
    endDate: string;
    numberOfDevices: number;
}

/**
 * Interface for VRF configuration in Telco mode
 */
// Interface for VRF form data (from UI)
export interface VRFFormData extends NexthopRetryFormValues {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[];
  enis: string[];
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection for Telco mode
  inboundId?: number | null; // Add inboundId for Telco mode updates
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down
}

export interface VRFConfig {
  vrfName: string;
  routeTargets: string;
  routingTableType: "specific" | "dynamic";
  routingTables?: string[];
  routingTagName?: string;
  routingTagValue?: string;
  nextHopType: "ENI" | "TGW" | "TAG" | "DYNAMIC_ENI_RESOLVER";
  eni?: string; // Only when nextHopType is "ENI"
  tgw?: string; // Only when nextHopType is "TGW"
  nextHopTagName?: string;
  nextHopTagValue?: string;
  mode: "TELCO" | "ENTERPRISE";
  vpcId?: string; // Add vpcId field for each VRF
  inboundId?: number | null; // Add inboundId for Telco mode updates
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down
  // Next-hop resolution retry; omitted for reflectors below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
  nexthopRetry?: NexthopRetryPayload;
}

/**
 * Interface for inbound propagation rule payload
 * For CREATE: uses bgpNeighborIds array (standalone mode)
 */
export interface InboundPropagationRulePayload {
  propagationRuleId?: string; // UUID for UPDATE (omit for CREATE)
  name?: string; // Display name for the rule
  bgpNeighborIds: number[]; // Array of BGP neighbor IDs for CREATE
  mode?: "TELCO" | "ENTERPRISE";
  inboundId?: number | null; // For ENTERPRISE updates (single inbound)
  // Single VRF fields (for backward compatibility)
  vrfName?: string; // Only for TELCO mode
  routeTargets?: string; // For both TELCO and ENTERPRISE modes
  // Multiple VRF fields (for Telco mode with multiple VRFs)
  vrfs?: VRFConfig[]; // Only for TELCO mode with multiple VRFs
  // Common fields (optional for Telco mode with multiple VRFs)
  routingTableType?: "specific" | "dynamic";
  routingTables?: string[]; // Only when routingTableType is "specific"
  routingTagName?: string; // Only when routingTableType is "dynamic"
  routingTagValue?: string; // Only when routingTableType is "dynamic"
  nextHopType?: "ENI" | "TGW" | "TAG" | "DYNAMIC_ENI_RESOLVER";
  eni?: string; // Only when nextHopType is "ENI"
  tgw?: string; // Only when nextHopType is "TGW"
  nextHopTagName?: string; // Only when nextHopType is "TAG"
  nextHopTagValue?: string; // Only when nextHopType is "TAG"
  vpcId?: string; // VPC selection for both Enterprise and Telco modes
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down (ENTERPRISE flat payload)
  // Next-hop resolution retry; omitted for reflectors below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
  nexthopRetry?: NexthopRetryPayload;
}

export interface OutboundTagPair {
  tagName: string;
  tagValue: string;
}

/**
 * Interface for outbound propagation rule payload
 * For CREATE: uses bgpNeighborIds array (standalone mode)
 */
/** Single outbound entry inside top-level `outbounds[]` (KD-612 contract) */
export interface OutboundRuleItemPayload {
  outboundId?: number;
  name?: string;
  vpcId?: string;
  subnetConfigType: "manual" | "subnet_tags" | "routing_table";
  subnets?: string[];
  subnetTags?: OutboundTagPair[];
  routingTableTags?: OutboundTagPair[];
  routingTableSubnetTags?: OutboundTagPair[];
  filterSubnetsWithinRoutingTables?: boolean;
  bgpCommunity?: string;
  nexthopIPAddress?: string;
}

/** CREATE/UPDATE payload — one request with all outbound rules */
export interface OutboundPropagationRulesPayload {
  propagationRuleId?: string;
  /** PropagationRule display name (top-level; distinct from each outbound's `name`) */
  name?: string;
  bgpNeighborIds: number[];
  mode: "TELCO" | "ENTERPRISE";
  outbounds: OutboundRuleItemPayload[];
}

/** @deprecated Legacy flat single-rule payload — use OutboundPropagationRulesPayload */
export interface OutboundPropagationRulePayload {
  propagationRuleId?: string;
  name?: string;
  bgpNeighborIds: number[];
  mode: "TELCO" | "ENTERPRISE";
  bgpCommunity?: string;
  tagName?: string;
  tagValue?: string;
  nexthopIPAddress?: string;
  vpcId?: string;
  subnetConfigType?: "manual" | "subnet_tags" | "routing_table";
  subnetSelectionType?: "manual" | "subnet_tags" | "routing_table";
  subnets?: string[];
  outboundSubnetTags?: OutboundTagPair[];
  subnetTags?: OutboundTagPair[];
  outboundRoutingTableTags?: OutboundTagPair[];
  routingTableTags?: OutboundTagPair[];
  filterSubnetsWithinRoutingTables?: boolean;
  filterSubnetsInRoutingTables?: boolean;
  subnetFilterTags?: OutboundTagPair[];
}

/**
 * Helper function to build inbound propagation rule payload based on form values
 * For CREATE: uses bgpNeighborIds array (do not pass propagationRuleId).
 * For UPDATE: pass propagationRuleId (UUID string) to include in payload.
 */
export function buildInboundPropagationRulePayload(
  bgpNeighborIds: number[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any,
  isTelco = false,
  propagationRuleId?: string
): InboundPropagationRulePayload {
  const addRuleId = (p: InboundPropagationRulePayload) => {
    if (propagationRuleId) p.propagationRuleId = propagationRuleId;
    if (values.name != null && String(values.name).trim() !== '') p.name = String(values.name).trim();
    return p;
  };
  // Add TELCO-specific fields
  if (isTelco) {
    if (values.vrfs && values.vrfs.length > 0) {
      // Handle multiple VRFs
      if (values.vrfs.length > 1) {
        // Multiple VRFs - create array of VRF configurations
        const vrfConfigs: VRFConfig[] = values.vrfs.map((vrf: VRFFormData) => {
          const vrfConfig: VRFConfig = {
            vrfName: vrf.name,
            routeTargets: vrf.routeTargets.join(", "),
            routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
            nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
            mode: isTelco ? "TELCO" : "ENTERPRISE",
            vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
            inboundId: vrf.inboundId || null, // Add inboundId for updates
            persistence: vrf.persistence ?? true, // Hold route on peer down (default true)
            // Next-hop retry or verification (CTX-181/CTX-347); absent when untouched or below the version gates
            nexthopRetry: buildNexthopSettingsPayload(vrf, values),
          };
          
          // Handle routing table configuration
          if (vrf.policySelection === "specific") {
            // Filter out invalid routing tables
            vrfConfig.routingTables = validateAndFilterRoutingTables(vrf.routingTables || []);
          } else {
            vrfConfig.routingTagName = vrf.routingTableTagName;
            vrfConfig.routingTagValue = vrf.routingTableTagValue;
          }
          
          // Handle next-hop configuration
          if (vrf.nextHopSelection === "dynamic_eni_resolver") {
            // Dynamic ENI Resolver: ENI is resolved live from BGP, nothing to send.
            vrfConfig.nextHopType = "DYNAMIC_ENI_RESOLVER";
          } else if (vrf.nextHopSelection === "specific") {
            // Get the selected value (could be ENI or TGW)
            const selectedValue = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : (vrf.selectedEni || "");

            // Determine nextHopType based on prefix
            if (selectedValue.toLowerCase().startsWith("eni")) {
              vrfConfig.nextHopType = "ENI";
              vrfConfig.eni = selectedValue;
            } else if (selectedValue.toLowerCase().startsWith("tgw")) {
              vrfConfig.nextHopType = "TGW";
              vrfConfig.tgw = selectedValue;
            } else {
              // Default to ENI if prefix doesn't match (backward compatibility)
              vrfConfig.nextHopType = "ENI";
              vrfConfig.eni = selectedValue;
            }
          } else {
            vrfConfig.nextHopTagName = vrf.nextHopTagName;
            vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
          }

          return vrfConfig;
        });
        
        return addRuleId({
          bgpNeighborIds: bgpNeighborIds,
          mode: "TELCO",
          vrfs: vrfConfigs,
        });
      } else {
        // Single VRF - always use vrfs array format for Telco mode
        const vrf: VRFFormData = values.vrfs[0];
        
        const vrfConfig: VRFConfig = {
          vrfName: vrf.name,
          routeTargets: vrf.routeTargets.join(", "),
          routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
          nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
          mode: "TELCO",
          vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
          inboundId: vrf.inboundId || null, // Add inboundId for updates
          persistence: vrf.persistence ?? true, // Hold route on peer down (default true)
          // Next-hop retry or verification (CTX-181/CTX-347); absent when untouched or below the version gates
          nexthopRetry: buildNexthopSettingsPayload(vrf, values),
        };
        
        // Handle routing table configuration
        if (vrf.policySelection === "specific") {
          // Filter out invalid routing tables
          vrfConfig.routingTables = validateAndFilterRoutingTables(vrf.routingTables || []);
        } else {
          vrfConfig.routingTagName = vrf.routingTableTagName;
          vrfConfig.routingTagValue = vrf.routingTableTagValue;
        }
        
        // Handle next-hop configuration
        if (vrf.nextHopSelection === "dynamic_eni_resolver") {
          // Dynamic ENI Resolver: ENI is resolved live from BGP, nothing to send.
          vrfConfig.nextHopType = "DYNAMIC_ENI_RESOLVER";
        } else if (vrf.nextHopSelection === "specific") {
          // For Telco mode, send only the first ENI as a string
          vrfConfig.eni = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : undefined;
        } else {
          vrfConfig.nextHopTagName = vrf.nextHopTagName;
          vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
        }
        
        return addRuleId({
          bgpNeighborIds: bgpNeighborIds,
          mode: "TELCO",
          vrfs: [vrfConfig],
        });
      }
    }
  }
  
  // Enterprise mode: CREATE and UPDATE both use flat payload (propagationRuleId added for UPDATE)
  if (!propagationRuleId) {
    // CREATE: original flat payload
    const basePayload: InboundPropagationRulePayload = {
      bgpNeighborIds: bgpNeighborIds,
      mode: "ENTERPRISE",
      routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
      nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
      persistence: values.persistence ?? true, // Hold route on peer down (default true)
      // Next-hop retry or verification (CTX-181/CTX-347); absent when untouched or below the version gates
      nexthopRetry: buildNexthopSettingsPayload(values, values),
    };
    if (values.routeTargets && values.routeTargets.length > 0) {
      basePayload.routeTargets = values.routeTargets.join(", ");
    }
    if (values.policySelection === "specific") {
      basePayload.routingTables = validateAndFilterRoutingTables(values.routingTables || []);
    } else {
      basePayload.routingTagName = values.routingTableTagName;
      basePayload.routingTagValue = values.routingTableTagValue;
    }
    if (values.selectedVpc) basePayload.vpcId = values.selectedVpc;
    if (values.nextHopSelection === "dynamic_eni_resolver") {
      basePayload.nextHopType = "DYNAMIC_ENI_RESOLVER";
    } else if (values.nextHopSelection === "specific") {
      const selectedValue = values.enis && values.enis.length > 0 ? values.enis[0] : (values.selectedEni || "");
      if (selectedValue.toLowerCase().startsWith("eni")) {
        basePayload.nextHopType = "ENI";
        basePayload.eni = selectedValue;
      } else if (selectedValue.toLowerCase().startsWith("tgw")) {
        basePayload.nextHopType = "TGW";
        basePayload.tgw = selectedValue;
      } else {
        basePayload.nextHopType = "ENI";
        basePayload.eni = selectedValue;
      }
    } else {
      basePayload.nextHopTagName = values.nextHopTagName;
      basePayload.nextHopTagValue = values.nextHopTagValue;
    }
    return addRuleId(basePayload);
  }

  // UPDATE: flat payload (same level as propagationRuleId), no vrfs wrapper
  const updatePayload: InboundPropagationRulePayload = {
    bgpNeighborIds: bgpNeighborIds,
    mode: "ENTERPRISE",
    routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
    nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
    persistence: values.persistence ?? true, // Hold route on peer down (default true)
    // Next-hop retry or verification (CTX-181/CTX-347); absent when untouched or below the version gates
    nexthopRetry: buildNexthopSettingsPayload(values, values),
  };
  if (values.routeTargets && values.routeTargets.length > 0) {
    updatePayload.routeTargets = values.routeTargets.join(", ");
  }
  if (values.inboundId != null) updatePayload.inboundId = values.inboundId;
  if (values.policySelection === "specific") {
    updatePayload.routingTables = validateAndFilterRoutingTables(values.routingTables || []);
  } else {
    updatePayload.routingTagName = values.routingTableTagName;
    updatePayload.routingTagValue = values.routingTableTagValue;
  }
  if (values.selectedVpc) updatePayload.vpcId = values.selectedVpc;
  if (values.nextHopSelection === "dynamic_eni_resolver") {
    updatePayload.nextHopType = "DYNAMIC_ENI_RESOLVER";
  } else if (values.nextHopSelection === "specific") {
    const selectedValue = values.enis && values.enis.length > 0 ? values.enis[0] : (values.selectedEni || "");
    if (selectedValue.toLowerCase().startsWith("eni")) {
      updatePayload.nextHopType = "ENI";
      updatePayload.eni = selectedValue;
    } else if (selectedValue.toLowerCase().startsWith("tgw")) {
      updatePayload.nextHopType = "TGW";
      updatePayload.tgw = selectedValue;
    } else {
      updatePayload.nextHopType = "ENI";
      updatePayload.eni = selectedValue;
    }
  } else {
    updatePayload.nextHopTagName = values.nextHopTagName;
    updatePayload.nextHopTagValue = values.nextHopTagValue;
  }
  return addRuleId(updatePayload);
}

/**
 * Pass the full subnet_formatted string to the API.
 * The backend EndorConfigBuilder regex handles all formats:
 *   - "subnet-xxx - name (cidr)" → extracts subnet_id + cidr
 *   - "subnet-xxx (cidr)" → extracts subnet_id + cidr
 *   - "subnet-xxx" (bare ID) → cidr: null (backward compatible)
 */
function formatSubnetForApi(formattedSubnet: string): string {
  if (!formattedSubnet) return '';
  return formattedSubnet.trim();
}

function filterCompleteTagPairs(pairs: { tagName?: string; tagValue?: string }[] | undefined) {
  return (pairs || [])
    .filter(
      (p): p is { tagName: string; tagValue: string } =>
        !!(p.tagName?.trim() && p.tagValue?.trim())
    )
    .map((p) => ({ tagName: p.tagName.trim(), tagValue: p.tagValue.trim() }));
}

/**
 * Builds a single outbound rule object for the `outbounds[]` array.
 */
export function buildOutboundRuleItemPayload(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any
): OutboundRuleItemPayload {
  const mode = values.subnetConfigMode || "manual";
  const item: OutboundRuleItemPayload = { subnetConfigType: mode };

  if (values.outboundId != null) {
    item.outboundId = values.outboundId;
  }

  if (values.name != null && String(values.name).trim() !== "") {
    item.name = String(values.name).trim();
  }

  if (mode === "manual") {
    item.subnets = (values.subnets || []).map((subnet: string) => formatSubnetForApi(subnet));
  } else if (mode === "subnet_tags") {
    item.subnetTags = filterCompleteTagPairs(values.subnetTags);
  } else if (mode === "routing_table") {
    item.routingTableTags = filterCompleteTagPairs(values.routingTableTags);
    item.filterSubnetsWithinRoutingTables = !!values.filterSubnetsInRoutingTables;
    if (values.filterSubnetsInRoutingTables) {
      item.routingTableSubnetTags = filterCompleteTagPairs(values.subnetFilterTags);
    }
  }

  if (values.bgpCommunity != null && String(values.bgpCommunity).trim() !== "") {
    item.bgpCommunity = values.bgpCommunity;
  }
  if (values.nexthopIPAddress) {
    item.nexthopIPAddress = values.nexthopIPAddress;
  }
  if (values.selectedVpc) {
    item.vpcId = values.selectedVpc;
  }

  return item;
}

/**
 * Builds outbound CREATE/UPDATE payload with all rules in `outbounds[]`.
 */
export function buildOutboundPropagationRulesPayload(
  bgpNeighborIds: number[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any[],
  isTelco = false,
  propagationRuleId?: string,
  defaultRuleName?: string
): OutboundPropagationRulesPayload {
  const payload: OutboundPropagationRulesPayload = {
    bgpNeighborIds,
    mode: isTelco ? "TELCO" : "ENTERPRISE",
    outbounds: rules.map((rule) => buildOutboundRuleItemPayload(rule)),
  };
  if (propagationRuleId) payload.propagationRuleId = propagationRuleId;
  if (defaultRuleName != null && String(defaultRuleName).trim() !== "") {
    payload.name = String(defaultRuleName).trim();
  }
  return payload;
}

/**
 * @deprecated Use buildOutboundPropagationRulesPayload — builds legacy flat single-rule payload.
 */
export function buildOutboundPropagationRulePayload(
  bgpNeighborIds: number[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any,
  isTelco = false,
  propagationRuleId?: string
): OutboundPropagationRulePayload {
  const item = buildOutboundRuleItemPayload(values);
  const basePayload: OutboundPropagationRulePayload = {
    bgpNeighborIds,
    mode: isTelco ? "TELCO" : "ENTERPRISE",
    subnetConfigType: item.subnetConfigType,
    ...(item.name ? { name: item.name } : {}),
    ...(item.subnets ? { subnets: item.subnets } : {}),
    ...(item.subnetTags ? { subnetTags: item.subnetTags, outboundSubnetTags: item.subnetTags } : {}),
    ...(item.routingTableTags
      ? { routingTableTags: item.routingTableTags, outboundRoutingTableTags: item.routingTableTags }
      : {}),
    ...(item.filterSubnetsWithinRoutingTables != null
      ? { filterSubnetsWithinRoutingTables: item.filterSubnetsWithinRoutingTables }
      : {}),
    ...(item.routingTableSubnetTags
      ? { subnetFilterTags: item.routingTableSubnetTags, outboundSubnetTags: item.routingTableSubnetTags }
      : {}),
    ...(item.bgpCommunity ? { bgpCommunity: item.bgpCommunity } : {}),
    ...(item.nexthopIPAddress ? { nexthopIPAddress: item.nexthopIPAddress } : {}),
    ...(item.vpcId ? { vpcId: item.vpcId } : {}),
  };
  if (propagationRuleId) basePayload.propagationRuleId = propagationRuleId;
  return basePayload;
}

/*** 
 * GET /instances
 * Lists all ReflectorInstances with cluster information.
 */
export function getReflectorsList(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/{id}/overview
  Gets complete details of a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getReflectorDetails(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/overview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/{id}/cloud-elements
 * Gets cloud elements for a specific ReflectorInstance with pagination.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @param filter - Optional search filter value
 */
export function getReflectorCloudElements(token: string, reflectorId: number, tenantId: number, page = 1, limit = 20, filter?: string) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString(),
        page: page.toString(),
        limit: limit.toString()
    });
    if (filter && filter.trim() !== '') {
        params.append('filter', filter.trim());
    }
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/cloud-elements?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

//BGP CONFIG

/**
 * GET /instances/:id/bgp-config
 * Gets BGP configuration of a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getReflectorBgpConfig(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-config?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** PUT /instances/:id/bgp-config
    Updates the global BGP configuration and automatically generates hash.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param payload - BGP configuration payload
 * @param tenantId - Tenant ID
 */
export function updateReflectorBgpConfig(token: string, reflectorId: number, payload: ReflectorBgpConfig, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-config?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * POST /instances/:id/bgp-neighbors
 *  Creates a new BGP neighbor and automatically generates hash.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param payload - BGP neighbor payload
 * @param tenantId - Tenant ID
 */
export function createReflectorBgpNeighbor(token: string, reflectorId: number, payload: ReflectorBgpNeighbor, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-neighbors?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** POST v2/reflector/bgp-neighbors/:id
 *  Creates a new BGP Neighbor on id we can send cluster or reflector ID
 * @param token - Authentication token
 * @param Id - Reflector or Cluster ID
 * @param payload - BGP neighbor payload
 * @param tenantId - Tenant ID
 */
export function createBgpNeighbor(token: string, Id: number, payload: BgpNeighbor, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${Id}?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT v2/reflector/bgp-neighbors/:Id
 * Update BGP Neighbor on id we can send cluster or reflector ID
 * @param token - Authentication token
 * @param Id - Reflector or Cluster ID
 * @param payload - BGP neighbor payload
 * @param tenantId - Tenant ID
 */
export function updateBgpNeighbor(token: string, Id: number, payload: BgpNeighbor, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/bgp-neighbors/${Id}?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/:id/bgp-neighbors
    Gets list of BGP neighbors with their associated prefixes.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getReflectorBgpNeighbors(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-neighbors?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * GET /v2/reflector/bgp-neighbor/:id
 * Gets a single BGP neighbor by ID with all details.
 * @param token - Authentication token
 * @param neighborId - BGP neighbor ID
 * @param tenantId - Tenant ID
 */
export function getBgpNeighborById(token: string, neighborId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/bgp-neighbor/${neighborId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** DELETE /instances/:id/bgp-neighbors/:neighborId
    Deletes a BGP neighbor.
 * @param token - Authentication token
 * @param neighborId - BGP neighbor ID
 * @param tenantId - Tenant ID
 */
export function deleteReflectorBgpNeighbor(token: string, neighborId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/bgp-neighbor/${neighborId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/:id/interfaces
 * Gets list of interfaces for a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getReflectorInterfaces(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/interfaces?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT /v2/reflector/instances/:id/name
 * Updates the name of a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param payload - Name payload
 * @param tenantId - Tenant ID
 */
export function updateReflectorName(token: string, reflectorId: number, payload: { name: string }, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/name?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT v2/reflector/instances/:id/tags
 * Updates the tags of a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param payload - Tags payload
 * @param tenantId - Tenant ID
 */
export function updateReflectorTags(token: string, reflectorId: number, payload: { tags: string[] }, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/tags?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}



//CLOUD PROPAGATION

/**
 * GET /instances/:id/cloud-propagation-rules
 * Gets cloud propagation rules for the instance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getCloudPropagationRules(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/propagation-rules/grouped?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v2/reflector/clusters/:clusterId/propagation-rules
 * Gets cloud propagation rules for cluster
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getCloudPropagationRulesByCluster(token: string, clusterId:number, tenantId: number){
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/grouped?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules
 * Gets cloud propagation rule details for a specific BGP neighbor.
 * @param token - Authentication token
 * @param bgpNeighborId - BGP neighbor ID
 * @param tenantId - Tenant ID
 */
export function getCloudPropagationRuleDetails(token: string, bgpNeighborId:number, tenantId: number){
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * CREATE INBOUND Propagation Rule Reflector Standalone
 * POST /v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules/inbound
 * @param token - Authentication token
 * @param bgpNeighborId - BGP neighbor ID (used in path, typically first ID from bgpNeighborIds array)
 * @param payload - Inbound propagation rule payload (must include bgpNeighborIds array)
 * @param tenantId - Tenant ID
 */
export function createInboundPropagationRule(token: string, bgpNeighborId: number, payload: InboundPropagationRulePayload, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules/inbound?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * CREATE OUTBOUND Propagation Rule Reflector Standalone
 * POST /v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules/outbound
 * @param token - Authentication token
 * @param bgpNeighborId - BGP neighbor ID (used in path, typically first ID from bgpNeighborIds array)
 * @param payload - Outbound propagation rule payload (must include bgpNeighborIds array)
 * @param tenantId - Tenant ID
 */
export function createOutboundPropagationRule(token: string, bgpNeighborId: number, payload: OutboundPropagationRulesPayload, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules/outbound?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT /v2/reflector/propagation-rules/:propagationRuleId/inbound/:inboundId
 * Update inbound propagation rule
 * @param token - Authentication token
 * @param propagationRuleId - Propagation rule ID
 * @param inboundId - Inbound rule ID
 * @param payload - Inbound propagation rule payload (for UPDATE, payload structure may differ from CREATE)
 * @param tenantId - Tenant ID
 */
export function updateInboundPropagationRule(token: string, propagationRuleId: number | string, inboundId: number, payload: Partial<InboundPropagationRulePayload>, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}/inbounds?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * PUT /v2/reflector/propagation-rules/:propagationRuleId/outbounds
 * Update all outbound propagation rules in one request
 */
export function updateOutboundPropagationRules(
  token: string,
  propagationRuleId: number | string,
  payload: Partial<OutboundPropagationRulesPayload>,
  tenantId: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
  });
  const ruleIdStr = String(propagationRuleId);
  const body = { ...payload, propagationRuleId: ruleIdStr };
  return axios.put(
    `${API_URL}/v2/reflector/propagation-rules/${ruleIdStr}/outbounds?${params.toString()}`,
    body,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * PUT /v2/reflector/propagation-rules/:propagationRuleId/outbound/:outboundId
 * @deprecated Per-rule update — prefer updateOutboundPropagationRules with outbounds[]
 */
export function updateOutboundPropagationRule(
  token: string,
  propagationRuleId: number | string,
  outboundId: number,
  payload: Partial<OutboundPropagationRulePayload>,
  tenantId: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
  });
  return axios.put(
    `${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}/outbound/${outboundId}?${params.toString()}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * DELETE /v2/reflector/propagation-rules/:propagationRuleId/inbound/:inboundId
 * Deletes a VRF (inbound propagation rule) for standalone mode
 * @param token - Authentication token
 * @param propagationRuleId - Propagation rule ID
 * @param inboundId - Inbound rule ID
 * @param tenantId - Tenant ID
 */
export function deleteInboundPropagationRule(token: string, propagationRuleId: number | string, inboundId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}/inbound/${inboundId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * DELETE /v2/reflector/propagation-rules/:propagationRuleId/outbound/:outboundId
 * Deletes a single outbound propagation rule (standalone mode)
 */
export function deleteOutboundPropagationRule(token: string, propagationRuleId: number | string, outboundId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}/outbound/${outboundId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}


/** 
 * 
 * NEW FUNCTIONS FOR CLOUD PROPAGATION
 * REFACTOR FOR NEW LOGIC
 */

/**
 * GET /v2/reflector/propagation-rules/:id
 * Gets a propagation rule by ID (numeric or UUID string)
 * @param token - Authentication token
 * @param propagationRuleId - Propagation rule ID (number or UUID string)
 * @param tenantId - Tenant ID
 */
export function getPropagationRuleById(token: string, propagationRuleId: number | string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/propagation-rules/uuid/${propagationRuleId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * DELETE /v2/reflector/propagation-rules/{UUID}
 * Deletes a propagation rule by UUID (standalone, no BGP neighbor or cluster context).
 */
export function deletePropagationRule(token: string, propagationRuleId: number | string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * DELETE /v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules/{UUID}
 * Deletes a propagation rule by UUID in BGP neighbor context.
 */
export function deletePropagationRuleByBgpNeighbor(token: string, bgpNeighborId: number, propagationRuleId: number | string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules/${propagationRuleId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/cloudelements/instances/:reflectorId
 * Gets route tables and network interfaces for a reflector
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getRouteTablesAndNetworkInterfaces(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/cloudelements/instances/${reflectorId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**GET v2/reflector/cloudelements/clusters/:clusterId 
 * Gets route tables and network interfaces for a cluster
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getRouteTablesAndNetworkInterfacesForCluster(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/cloudelements/instances/:reflectorId/subnets
 * Gets subnets for a reflector
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getSubnetsForReflector(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/cloudelements/instances/${reflectorId}/subnets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/** GET /v2/reflector/cloudelements/clusters/:clusterId/subnets
 * Gets subnets for a cluster
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getSubnetsForCluster(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}/subnets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * Element types the tag-keys endpoints accept.
 *
 * `network_interface` backs the inbound next-hop tag dropdown: an inbound rule's
 * next hop is always an ENI, so its tag keys must come from the interfaces rather
 * than the subnets (CTX-287). The backend allowlist has to name the same three
 * values — it answers 400 for anything else.
 */
export type CloudElementTagKeyType = "subnet" | "route_table" | "network_interface";

/**
 * GET /instances/:id/cloud-elements/tag-keys
 * Tag keys for CPR tag-name dropdowns (standalone reflector instance).
 * @param elementType - Optional filter: `subnet` | `route_table` | `network_interface`
 */
export function getCloudElementTagKeysForInstance(
  token: string,
  reflectorId: number,
  tenantId: number,
  elementType?: CloudElementTagKeyType
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    if (elementType) {
        params.append("elementType", elementType);
    }
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/cloud-elements/tag-keys?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /clusters/:id/cloud-elements/tag-keys
 * Tag keys for CPR tag-name dropdowns (cluster mode).
 * @param elementType - Optional filter: `subnet` | `route_table` | `network_interface`
 */
export function getCloudElementTagKeysForCluster(
  token: string,
  clusterId: number,
  tenantId: number,
  elementType?: CloudElementTagKeyType
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    if (elementType) {
        params.append("elementType", elementType);
    }
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/cloud-elements/tag-keys?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v2/reflector/visibility/sidebar
 * Gets cloud inventory sidebar data for BGP visibility page
 * @param token - Authentication token
 * @param tenantId - Tenant ID
 */
export function getCloudInventorySidebar(token: string, tenantId: number) {
    if (MOCK_BGP) {
        return import("../../BGP/__mocks__/visibilityPayload").then((m) => ({ data: m.MOCK_SIDEBAR }));
    }
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });

    return axios.get(`${API_URL}/v2/reflector/visibility/sidebar?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/visibility
 * Gets data for BGP visibility page cards 
 * @param token - Authentication token
 * @param type - Entity type: 'reflector' or 'cluster'
 * @param nameOrId - Reflector name (for type='reflector') or cluster ID (for type='cluster')
 * @param direction - Direction: 'inbound' or 'outbound'
 * @param tenantId - Tenant ID
 * @param signal - Optional abort signal, so a superseded selection can cancel its request (CTX-276)
 */
export function getBgpVisibilityData(token: string, type: 'reflector' | 'cluster', nameOrId: string, direction: 'inbound' | 'outbound', tenantId: number, signal?: AbortSignal) {
    if (MOCK_BGP) {
        // Sample payload is INBOUND-only; OUTBOUND correctly renders the "no routes" state.
        if (direction === "outbound") {
            return Promise.resolve({ data: [] });
        }
        return import("../../BGP/__mocks__/visibilityPayload").then((m) => ({
            data: MOCK_BGP_VOLUME ? m.generateHighVolumeVisibility() : m.MOCK_VISIBILITY_DEV
        }));
    }
    const params = new URLSearchParams({
        type,
        id: nameOrId, // Use 'id' as key, but value is reflector name for reflectors or cluster ID for clusters
        direction,
        tenantId: tenantId.toString()
    });

    return axios.get(`${API_URL}/v2/reflector/visibility?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
}

/** The `{ data: { items, pagination } }` envelope `GET /visibility/routes` answers with. */
export interface VisibilityRoutesResponse {
    items: unknown[];
    pagination: { page: number; page_size: number; total: number };
}

/** GET v2/reflector/visibility/routes
 * Gets the normalized, server-filtered route rows that feed BGP Visibility's Table.
 *
 * The flat counterpart of `/visibility`: one row per (prefix, route-table/subnet, neighbor)
 * instead of the whole per-member S3 document, so the table does not download four copies of
 * every prefix to render one (CTX-602).
 *
 * @param token - Authentication token
 * @param type - Entity type: 'reflector' or 'cluster'
 * @param nameOrId - Reflector subId (for type='reflector') or cluster subId (for type='cluster')
 * @param direction - Direction: 'inbound' or 'outbound'
 * @param tenantId - Tenant ID
 * @param serverFilters - Query params the endpoint applies itself (see `toServerRouteFilters`)
 * @param signal - Optional abort signal, so a superseded selection can cancel its request (CTX-276)
 * @returns the rows and their pagination, or `null` in fixture builds — `REACT_APP_MOCK_BGP`
 *   only ships `/visibility` payloads, so the caller keeps deriving rows client-side there.
 */
export async function getBgpVisibilityRoutes(
    token: string,
    type: 'reflector' | 'cluster',
    nameOrId: string,
    direction: 'inbound' | 'outbound',
    tenantId: number,
    serverFilters: Record<string, string> = {},
    signal?: AbortSignal
): Promise<VisibilityRoutesResponse | null> {
    if (MOCK_BGP) {
        return null;
    }
    const params = new URLSearchParams({
        type,
        id: nameOrId,
        direction,
        tenantId: tenantId.toString(),
        ...serverFilters
    });

    const response = await axios.get(`${API_URL}/v2/reflector/visibility/routes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });

    const payload = response.data?.data;
    return {
        items: Array.isArray(payload?.items) ? payload.items : [],
        pagination: payload?.pagination ?? { page: 1, page_size: 0, total: 0 }
    };
}

/** GET v2/reflector/visibility/neighbordata
 * Gets neighbor data for BGP visibility page (status, peerAs, description, etc.)
 * @param token - Authentication token
 * @param type - Entity type: 'reflector' or 'cluster'
 * @param nameOrId - Reflector subId (for type='reflector') or cluster subId (for type='cluster')
 * @param tenantId - Tenant ID
 * @param signal - Optional abort signal, so a superseded selection can cancel its request (CTX-276)
 */
export function getBgpVisibilityNeighborData(token: string, type: 'reflector' | 'cluster', nameOrId: string, tenantId: number, signal?: AbortSignal) {
    if (MOCK_BGP) {
        return import("../../BGP/__mocks__/visibilityPayload").then((m) => ({ data: m.MOCK_NEIGHBOR_DATA }));
    }
    const params = new URLSearchParams({
        type,
        id: nameOrId,
        tenantId: tenantId.toString()
    });

    return axios.get(`${API_URL}/v2/reflector/visibility/neighbordata?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
}




//BGP NEIGHBORS
/**
 * GET /clusters/:id/bgp-neighbors
 * Gets list of BGP neighbors for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getClusterBgpNeighbors(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-neighbors/grouped?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /bgp-neighbors/dropdown
 * Gets BGP neighbors for dropdown selection
 * @param token - Authentication token
 * @param clusterId - Cluster ID (for cluster mode)
 * @param reflectorInstanceId - Instance/Reflector ID (for standalone mode)
 * @param mode - Mode filter: "ENTERPRISE" or "TELCO" (optional)
 * @returns Response with data array containing { id, subUuid, name, neighborAddress, displayText, peerAs }
 */
export function getBgpNeighborsDropdown(token: string, clusterId?: number, reflectorInstanceId?: number, mode?: "ENTERPRISE" | "TELCO") {
    const params = new URLSearchParams();
    if (clusterId) {
        params.append('clusterId', clusterId.toString());
    } else if (reflectorInstanceId) {
        params.append('reflectorInstanceId', reflectorInstanceId.toString());
    }
    // Add mode parameter if provided
    if (mode) {
        params.append('mode', mode);
    }
    return axios.get(`${API_URL}/v2/reflector/bgp-neighbors/dropdown?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}



//IAM PERMISSIONS

/**
 * GET /instances/:id/iam-permissions
 * Gets IAM permissions for a specific ReflectorInstance.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getReflectorIamPermissions(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/iam-permissions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

//LICENSING

/**POST /reflector/generate-token 
 * Generates a token for a specific reflector license.
*/
export function generateToken(token: string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/generate-token?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**POST /reflector/regenerate-token 
 * Regenerates a token for a specific reflector license.
*/
export function regenerateToken(token: string, tenantId: number, payload: { token: string }) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/generate-token?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**GET /reflector/license/list 
 * Lists all reflector licenses.
 * @param token - Authentication token
 * @param tenantId - Tenant ID
*/
export function getReflectorLicenses(token: string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/license/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * One reflector's license verdict, as returned by the CTX-345 validity endpoints.
 *
 * `reason` is part of the backend contract — the gate copy branches on it, so a new
 * value here means new copy, never a silent fallthrough.
 */
export type LicenseValidityReason =
    | 'valid'
    | 'expired'
    | 'suspended'
    | 'not_started'
    | 'no_license';

export interface LicenseValidityLicense {
    /** Null on the synthesized PAYG shape — PAYG has no ReflectorLicense row. */
    id: number | null;
    serial: string | null;
    type: string | null;
    status: number | null;
    startDate: string | null;
    endDate: string | null;
}

export interface ReflectorLicenseValidity {
    reflectorId: number;
    subId: string | null;
    name: string | null;
    role: string | null;
    valid: boolean;
    reason: LicenseValidityReason;
    /**
     * Explicit PAYG flag from CTX-345. The gate itself keys on `valid === false`, so
     * `payg: true` (always `valid: true`) passes through ungated; the flag is what
     * keeps expiry chrome from being rendered for a model that has no term.
     */
    payg: boolean;
    /**
     * Null when nothing is assigned (`no_license`). PAYG is a synthesized object
     * with `id` / `status` / dates null, `type: "PAYG"`, and the token as `serial`.
     */
    license: LicenseValidityLicense | null;
}

/** Cluster verdict: `valid` only while every member is licensed. */
export interface ClusterLicenseValidity {
    clusterId: number;
    valid: boolean;
    reason: LicenseValidityReason;
    /** True only when every member is PAYG — one BYOL member is enough to have a date. */
    payg: boolean;
    members: ReflectorLicenseValidity[];
}

/**GET /v2/reflector/instances/:id/license-validity
 * Whether this reflector's license is valid right now, with the reason and the license behind it.
 * Source of truth for the license gate — never derive validity from the inline `license` row.
 * @param token - Authentication token
 * @param reflectorId - Reflector instance ID
 * @param tenantId - Tenant ID
*/
export function getReflectorLicenseValidity(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get<{ success: boolean; message: string; data: ReflectorLicenseValidity }>(
        `${API_URL}/v2/reflector/instances/${reflectorId}/license-validity?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
    );
}

/**GET /v2/reflector/clusters/:id/license-validity
 * Per-member license validity plus the cluster's aggregate verdict, in a single request —
 * the gate covers a cluster as a whole, so it must not fan out one call per member.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
*/
export function getClusterLicenseValidity(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get<{ success: boolean; message: string; data: ClusterLicenseValidity }>(
        `${API_URL}/v2/reflector/clusters/${clusterId}/license-validity?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
    );
}

/**PUT /reflector/license/:id 
 * Updates a reflector license.
 * @param token - Authentication token
 * @param licenseId - License ID
 * @param payload - License payload (description, total, startDate, endDate, type, status, activated)
 * @param tenantId - Tenant ID
*/
export function updateReflectorLicense(token: string, licenseId: number, payload: { 
    description: string; 
    total: number; 
    startDate: string; 
    endDate: string; 
    type: string;
    status?: number;
    activated?: number;
}, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector-admin/license/${licenseId}?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**POST /reflector/license 
 * Creates a new reflector license.
 * @param token - Authentication token
 * @param payload - License payload (includes tenantId)
 * @param tenantId - Tenant ID (optional, can be in payload)
*/
export function createReflectorLicense(token: string, payload: ReflectorLicense, tenantId?: number) {
    const params = new URLSearchParams();
    if (tenantId !== undefined) {
        params.append('tenantId', tenantId.toString());
    } else if (payload.tenantId) {
        params.append('tenantId', payload.tenantId.toString());
    }
    const queryString = params.toString();
    const url = queryString ? `${API_URL}/v2/reflector/license?${queryString}` : `${API_URL}/v2/reflector/license`;
    return axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v1/tenant/customers?tenantId=xxx
 * Fetch customers for a specific tenant
 */
export function fetchCustomersWithTenant(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v1/tenant/customers?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v1/tenant/partners
 * Fetch partners
 */
export function fetchPartnersWithTenant(token: string) {
    return axios.get(`${API_URL}/v1/tenant/partners`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}






/** Códigos con los que el backend rechaza un borrado (CTX-538). */
export type DeleteReflectorCode =
    | "REFLECTOR_IN_CLUSTER"
    | "REFLECTOR_NOT_FOUND"
    | "REFLECTOR_DELETE_FAILED";

export interface DeleteReflectorResponse {
    deleted: boolean;
    reflector: { id: number; name: string };
    /** Contadores ya recalculados; null si el reflector no sostenia licencia. */
    license: {
        id: number;
        total: number | null;
        activated: number | null;
        available: number | null;
    } | null;
}

/**
 * DELETE /v2/reflector/instances/:id
 *
 * Borrado logico (CTX-538): el reflector desaparece de las vistas, pierde su
 * sesion de websocket y devuelve su asiento de licencia. Los datos se conservan
 * 90 dias — dentro de esa ventana, un claim del mismo appliance lo reactiva con
 * su configuracion intacta (CTX-604).
 *
 * Igual que la liberacion, un reflector que pertenece a un cluster se rechaza
 * con `REFLECTOR_IN_CLUSTER` para que el usuario sepa que deshacerlo primero.
 */
export function deleteReflectorInstance(
    token: string,
    reflectorId: string | number,
    tenantId: number
) {
    const params = new URLSearchParams({ tenantId: String(tenantId) });
    return axios.delete<DeleteReflectorResponse>(
        `${API_URL}/v2/reflector/instances/${reflectorId}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
    );
}

/** Códigos con los que el backend rechaza una liberación (CTX-351). */
export type ReleaseLicenseCode =
    | "REFLECTOR_IN_CLUSTER"
    | "PAYG_NOT_RELEASABLE"
    | "ALREADY_RELEASED"
    | "REFLECTOR_NOT_FOUND"
    | "RELEASE_FAILED";

export interface ReleaseLicenseResponse {
    released: boolean;
    code?: ReleaseLicenseCode;
    message?: string;
    license: {
        id: number;
        serial: string;
        total: number | null;
        activated: number | null;
        available: number | null;
    } | null;
}

/**
 * POST /v2/reflector/instances/:id/release-license
 *
 * Libera al reflector de su licencia BYOL y devuelve el cupo. El backend decide
 * si se puede: un reflector que pertenece a un cluster o que está licenciado por
 * PAYG se rechaza con su propio código, y "ya estaba liberado" llega como 200.
 */
export function releaseReflectorLicense(
    token: string,
    reflectorId: string | number,
    tenantId: number
) {
    const params = new URLSearchParams({ tenantId: String(tenantId) });
    return axios.post<ReleaseLicenseResponse>(
        `${API_URL}/v2/reflector/instances/${reflectorId}/release-license?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
    );
}
